import {Dialog, DialogContent, DialogTitle} from '@kroll-resolver-crisp/ui-components-react';
import {ImageOff} from 'lucide-react';
import {useState} from 'react';
import {Thumbnail} from '@/components/shared/Thumbnail';
import {usePlatformImageSrc} from '@/components/shared/usePlatformImageSrc';
import {cn} from '@/lib/utils';
import {useUiStore} from '@/store/uiStore';

// Full-screen image preview. Renders nothing unless open with a resolvable url. Escape, backdrop
// click and the close button all close it, which the design system's Dialog provides.
export function ImageLightbox({
  open,
  imageUrl,
  alt,
  revealed = false,
  colourised = false,
  onClose
}: {
  readonly open: boolean;
  readonly imageUrl: string | null;
  readonly alt: string;
  // The analyst already unblurred this image where they opened it from, so show it as it was
  // rather than blurring it again. Otherwise it follows the blur preference like any thumbnail.
  readonly revealed?: boolean;
  // The same for greyscale: the analyst already colourised it where they opened it from.
  readonly colourised?: boolean;
  readonly onClose: () => void;
}) {
  const blurThumbnails = useUiStore((state) => state.blurThumbnails);
  const greyscaleThumbnails = useUiStore((state) => state.greyscaleThumbnails);
  const unblurred = revealed || !blurThumbnails;
  // The unblurred path renders its own <img>, so it resolves the stored copy itself, as Thumbnail
  // does. Only then: the blurred path's Thumbnail does its own lookup, and two would fetch it twice.
  // The blurred path's Thumbnail reports a missing or unrenderable image through onError, keyed by
  // url so a different image starts fresh.
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const image = usePlatformImageSrc(open && unblurred ? (imageUrl ?? undefined) : undefined, null);

  if (!open || !imageUrl) {
    return null;
  }

  const unavailable = unblurred ? image.missing : failedUrl === imageUrl;
  const imageClass = 'max-h-[85vh] max-w-[85vw] object-contain';
  // Thumbnail applies greyscale from the preference on its own; `grayscale-0` is how a colourised
  // image opts out of that (tailwind-merge lets it win over `grayscale`).
  const colourClass = colourised && 'grayscale-0';

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) {
          onClose();
        }
      }}>
      <DialogContent
        className="w-fit max-w-[85vw] bg-black p-0 sm:max-w-[85vw]"
        aria-describedby={undefined}>
        <DialogTitle className="sr-only">{alt || 'Image preview'}</DialogTitle>
        {unavailable ? (
          <div className="text-muted-foreground flex min-h-48 min-w-64 flex-col items-center justify-center gap-2 p-8 text-sm">
            <ImageOff className="size-10" aria-hidden="true" />
            Image not available
          </div>
        ) : unblurred ? (
          <img
            src={image.src}
            alt={alt}
            referrerPolicy="no-referrer"
            className={cn(imageClass, greyscaleThumbnails && !colourised && 'grayscale')}
          />
        ) : (
          <Thumbnail
            src={imageUrl}
            alt={alt}
            className={cn(imageClass, colourClass)}
            onError={() => {
              setFailedUrl(imageUrl);
            }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
